import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from '@nk/access-block';
import { AuthInfo, AuthStatus, GENERIC_AUTH_SERVICE_TOKEN, GenericAuthService } from '@nk/auth-lib';
import { CachingService } from '@nk/caching-lib';
import { includes } from '@nk/utils-lib';
import { filter } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

@Component({
  templateUrl: 'identity-override.component.html',
})
export class IdentityOverrideComponent implements OnInit {
  public errorMessage: string;

  private _activatedRoute = inject(ActivatedRoute);
  private _appService = inject(AppService);
  private _router = inject(Router);
  private _cachingService = inject(CachingService);
  private _translate = inject(TranslateService);
  private _authService = inject<GenericAuthService>(GENERIC_AUTH_SERVICE_TOKEN);

  public ngOnInit(): void {
    this._activatedRoute.fragment
      .pipe(
        filter((a) => a && !!a.length),
        filter((a) => includes(a, 'access_token=')),
      )
      .subscribe((a) => {
        this._overrideAccessToken(a);
      });
  }

  private _overrideAccessToken(fragments: string): void {
    const urlParams = new URLSearchParams(fragments);
    const accessToken = urlParams.get('access_token');
    const customerSlug = urlParams.get('customer_slug');
    const redirectPath = urlParams.get('redirect_path');

    if (accessToken && customerSlug && redirectPath) {
      this._setNewAuth(accessToken, customerSlug, redirectPath);
    } else {
      this.errorMessage =
        'Missing required fragment, either access_token, customer_slug or redirect_path. Make sure to separate with &.';
      console.error('errorMessage', fragments);
    }
  }

  private _getExpirationFromAccessToken(accessToken: string): string {
    const now = new Date().getTime();
    const parsedHashList = accessToken
      .split('.')
      .splice(0, 2)
      .map((a) => atob(a))
      .map((a) => JSON.parse(a) as Record<string, unknown>);

    const exp = parsedHashList[1]?.['exp'];
    if (typeof exp !== 'number') {
      throw new Error('Could not parse expiration time from access token');
    }

    return JSON.stringify(now + exp);
  }

  private _createAuthInfo(accessToken: string): AuthInfo {
    const now = new Date().getTime();
    const expiresAt = this._getExpirationFromAccessToken(accessToken);
    const createdAt = JSON.stringify(now);

    return new AuthInfo(
      AuthStatus.Authenticated,
      expiresAt,
      createdAt,
      accessToken,
      null,
      null,
      'User is authenticated.',
    );
  }

  private _setNewAuth(accessToken: string, customerSlug: string, redirectPath: string): void {
    // We want to wait for first authinfo to load, which happens when auth service is initialized.
    this._authService.authInfo$
      .pipe(
        take(1),
        switchMap(() => {
          this._cachingService.invalidateAllCache();
          const newAuthInfo = this._createAuthInfo(accessToken);
          this._authService.setCustomerSlug(customerSlug);
          this._authService.authInfo = newAuthInfo;
          this._authService.overrideToken(newAuthInfo);
          // We fetch identity to ensure auth is OK
          return this._appService.identityDataCache.get();
        }),
      )
      .subscribe({
        next: (identity) => {
          this._translate.use(identity.app_user.language_code);
          this._router.navigateByUrl(redirectPath);
        },
        error: (error: unknown) => {
          if (error['error'] && error['error']['message']) {
            this.errorMessage = `API ERROR: ${error['error']['message']}`;
          } else {
            this.errorMessage = 'Could not change auth. Reason unknown.';
          }
        },
      });
  }
}
